import React from "react";
import {
  CardContent,
  Typography,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  CircularProgress
} from "@mui/material";
import { LocationOn } from "@mui/icons-material";
import { StyledCard } from "./styled";

const SearchConfigurationCard = ({
  title = "Search Configuration",
  location,
  onLocationChange,
  locationPlaceholder = "e.g., Traverse City, MI or 90210",
  locationHelperText = "Enter address, city, state, or zipcode",
  radiusMiles,
  onRadiusMilesChange,
  onRadiusMilesBlur,
  radiusInputProps = { min: 0.1, max: 31.1, step: 0.1 },
  radiusLabel = "Radius (miles)",
  radiusHelperText,
  clearExistingData,
  onToggleClearExistingData,
  showClearExistingToggle = true,
  clearExistingLabel = "Clear existing OSM data before fetching new data",
  clearExistingHelper = "This will remove all previously collected OSM data from your account",
  onFetch,
  isFormValid,
  loading,
  isListening,
  functionTimeout,
  progressCount,
  actionLabel = "Fetch OSM Data",
  actionLoadingLabel,
  actionStartIcon = <LocationOn />,
  actionDisabled,
  actionFullWidth = true,
  actionSize = "large",
  actionSx,
  actionsPlacement = "bottom" // "bottom" | "inline"
}) => {
  const renderActionText = () => {
    if (!loading) return actionLabel;
    if (actionLoadingLabel) return actionLoadingLabel;
    if (functionTimeout) return `Processing... (${progressCount} records found)`;
    return "Fetching Data...";
  };

  const button = (
    <Button
      variant="contained"
      color="primary"
      onClick={onFetch}
      disabled={actionDisabled ?? (!isFormValid || loading || isListening)}
      startIcon={loading ? <CircularProgress size={20} /> : actionStartIcon}
      fullWidth={actionFullWidth}
      size={actionSize}
      sx={actionSx}
    >
      {renderActionText()}
    </Button>
  );

  return (
    <StyledCard>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>

        <Grid container spacing={2} alignItems={actionsPlacement === "inline" ? "center" : "flex-start"}>
          <Grid item xs={12} md={actionsPlacement === "inline" ? 6 : 8}>
            <TextField
              fullWidth
              label="Location"
              value={location}
              onChange={(e) => onLocationChange(e.target.value)}
              placeholder={locationPlaceholder}
              helperText={locationHelperText}
              sx={{ mb: 2 }}
            />
          </Grid>

          <Grid item xs={12} md={actionsPlacement === "inline" ? 3 : 4}>
            <TextField
              fullWidth
              type="number"
              label={radiusLabel}
              value={radiusMiles}
              onChange={(e) => onRadiusMilesChange(e.target.value)}
              onBlur={onRadiusMilesBlur}
              inputProps={radiusInputProps}
              helperText={radiusHelperText}
              sx={{ mb: 2 }}
            />
          </Grid>

          {actionsPlacement === "inline" ? (
            <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
              {button}
            </Grid>
          ) : null}

          {showClearExistingToggle ? (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={clearExistingData}
                    onChange={(e) => onToggleClearExistingData(e.target.checked)}
                    color="warning"
                  />
                }
                label={clearExistingLabel}
              />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mt: 1 }}>
                {clearExistingHelper}
              </Typography>
            </Grid>
          ) : null}
        </Grid>

        {actionsPlacement !== "inline" ? button : null}
      </CardContent>
    </StyledCard>
  );
};

export default SearchConfigurationCard;
